import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, BrainCircuit, FileText, Globe2, Languages, SlidersHorizontal, Sparkles, Ticket, Upload, Volume2 } from 'lucide-react';

const featureKeys = [
  'soundLocalization',
  'remoteCompanion',
  'expressionSystem',
  'privacyProtection',
  'multimodalAI',
  'multilingual',
  'longTermMemory',
  'personalization',
  'motionSystem',
] as const;

type FeatureKey = (typeof featureKeys)[number];

interface FeatureView {
  scale: number;
  x: number;
  y: number;
}

const expressionOptions = [
  { key: 'happy', icon: '😊' },
  { key: 'winking', icon: '😉' },
  { key: 'goofy', icon: '🤪' },
  { key: 'surprised', icon: '😮' },
  { key: 'listening', icon: '🎧' },
  { key: 'sad', icon: '😢' },
  { key: 'angry', icon: '😠' },
  { key: 'sleep', icon: '😴' },
] as const;

type ExpressionKey = (typeof expressionOptions)[number]['key'];
type DisplayExpressionKey = ExpressionKey | 'record';

const expressionAssets: Record<DisplayExpressionKey, { left: string; right: string }> = {
  happy: { left: '/emoji/happy_r_loop.gif', right: '/emoji/happy_l_loop.gif' },
  winking: { left: '/emoji/winking_r_loop.gif', right: '/emoji/winking_l_loop.gif' },
  goofy: { left: '/emoji/goofy_r_loop.gif', right: '/emoji/goofy_l_loop.gif' },
  surprised: { left: '/emoji/surprised_r_loop.gif', right: '/emoji/surprised_l_loop.gif' },
  listening: { left: '/emoji/listening_r_loop.gif', right: '/emoji/listening_l_loop.gif' },
  sad: { left: '/emoji/sad_r_loop.gif', right: '/emoji/sad_l_loop.gif' },
  angry: { left: '/emoji/angry_r_loop.gif', right: '/emoji/angry_l_loop.gif' },
  sleep: { left: '/emoji/sleep_rl_loop.gif', right: '/emoji/sleep_rl_loop.gif' },
  record: { left: '/emoji/record_rl_loop.gif', right: '/emoji/record_rl_loop.gif' },
};

const featureViews: Record<FeatureKey, FeatureView> = {
  soundLocalization: { scale: 1.78, x: -4, y: 78 },
  remoteCompanion: { scale: 1.78, x: -4, y: 78 },
  expressionSystem: { scale: 2.02, x: 1, y: 65 },
  privacyProtection: { scale: 2.02, x: 1, y: 65 },
  multimodalAI: { scale: 1.78, x: -4, y: 78 },
  multilingual: { scale: 1.78, x: -4, y: 78 },
  longTermMemory: { scale: 1.78, x: -4, y: 78 },
  personalization: { scale: 1.78, x: -4, y: 78 },
  motionSystem: { scale: 1.62, x: 0, y: -27 },
};

const robotImage = '/images/wyndme-white-feature.png';
const robotImageWithEyes = '/images/wyndme-white-feature-eye.png';
const monitoringImages = {
  zh: '/images/monitoring-clean.png',
  en: '/images/monitoring-en.png',
  ja: '/images/monitoring-ja.png',
} as const;

const microphoneNodes = [
  { left: '15%', top: '48%', delay: '0ms', type: 'side-left', asset: 'side-left', pickup: 'left' },
  { left: '30%', top: '22%', delay: '180ms', type: 'rear-left', asset: 'rear-left', pickup: 'top' },
  { left: '26%', top: '37%', delay: '360ms', type: 'front-left', asset: 'front-left', pickup: 'top' },
  { left: '53%', top: '25%', delay: '540ms', type: 'rear-right', asset: 'rear-right', pickup: 'top' },
  { left: '47%', top: '40%', delay: '720ms', type: 'front-right', asset: 'front-right', pickup: 'top' },
  { left: '69%', top: '35%', delay: '900ms', type: 'side-right', asset: 'side-right', pickup: 'right' },
] as const;

const multimodalInputs = [
  { key: 'vision', left: '16%', top: '18%', delay: '0ms' },
  { key: 'voice', left: '17%', top: '31%', delay: '240ms' },
  { key: 'knowledge', left: '69%', top: '18%', delay: '480ms' },
  { key: 'memory', left: '69%', top: '31%', delay: '720ms' },
] as const;

const multilingualPhrases = [
  { text: '很高兴认识你呀', left: '17%', top: '26%', delay: '-0.4s' },
  { text: 'Nice to meet you!', left: '43%', top: '23%', delay: '-1.8s' },
  { text: 'はじめまして！', left: '69%', top: '26%', delay: '-2.8s' },
  { text: '¡Mucho gusto!', left: '21%', top: '39%', delay: '-3.6s' },
  { text: '만나서 반가워요!', left: '43%', top: '37%', delay: '-1s' },
  { text: 'Enchanté !', left: '65%', top: '39%', delay: '-2.2s' },
] as const;

type MultimodalInputKey = (typeof multimodalInputs)[number]['key'];

const MultimodalInputVisual: React.FC<{ type: MultimodalInputKey }> = ({ type }) => {
  if (type === 'vision') {
    return (
      <svg viewBox="0 0 72 46" className="h-full w-full" aria-hidden="true">
        <rect x="1" y="1" width="70" height="44" rx="9" fill="rgba(10,10,9,.92)" stroke="rgba(240,200,90,.42)" />
        <circle cx="55" cy="13" r="4" fill="rgba(255,218,108,.9)" />
        <path d="M5 39 23 20l11 11 10-8 23 17" fill="rgba(240,200,90,.13)" stroke="rgba(240,200,90,.62)" strokeWidth="1.3" />
        <path d="M7 9h8M7 9v8M65 9h-8M65 9v8M7 37h8M7 37v-8M65 37h-8M65 37v-8" fill="none" stroke="rgba(255,255,255,.5)" strokeWidth="1" />
        <path d="M8 26h56" stroke="rgba(240,200,90,.5)" strokeWidth=".8" strokeDasharray="2 3">
          <animate attributeName="d" values="M8 12h56;M8 36h56;M8 12h56" dur="3s" repeatCount="indefinite" />
        </path>
      </svg>
    );
  }

  if (type === 'voice') {
    return (
      <svg viewBox="0 0 72 46" className="h-full w-full" aria-hidden="true">
        <rect x="1" y="1" width="70" height="44" rx="22" fill="rgba(10,10,9,.92)" stroke="rgba(240,200,90,.32)" />
        {[8, 14, 21, 29, 36, 43, 51, 58, 64].map((x, index) => {
          const height = [8, 16, 25, 13, 31, 19, 27, 14, 8][index];
          return (
            <line key={x} x1={x} x2={x} y1={23 - height / 2} y2={23 + height / 2} stroke="rgba(240,200,90,.82)" strokeWidth="2.2" strokeLinecap="round">
              <animate attributeName="opacity" values=".35;1;.35" dur={`${1.1 + index * 0.08}s`} begin={`${index * 0.07}s`} repeatCount="indefinite" />
            </line>
          );
        })}
      </svg>
    );
  }

  if (type === 'knowledge') {
    return (
      <svg viewBox="0 0 72 46" className="h-full w-full" aria-hidden="true">
        <rect x="1" y="1" width="70" height="44" rx="22" fill="rgba(10,10,9,.92)" stroke="rgba(240,200,90,.32)" />
        <circle cx="36" cy="23" r="14" fill="none" stroke="rgba(240,200,90,.72)" strokeWidth="1.2" />
        <path d="M22 23h28M36 9c-6 6-6 22 0 28M36 9c6 6 6 22 0 28M25 15c7 4 15 4 22 0M25 31c7-4 15-4 22 0" fill="none" stroke="rgba(255,255,255,.32)" strokeWidth=".8" />
        <circle cx="21" cy="13" r="2.1" fill="rgba(255,218,108,.95)"><animate attributeName="r" values="1.3;2.5;1.3" dur="1.8s" repeatCount="indefinite" /></circle>
        <circle cx="53" cy="30" r="2" fill="rgba(255,218,108,.8)"><animate attributeName="r" values="2.4;1.2;2.4" dur="2.1s" repeatCount="indefinite" /></circle>
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 72 46" className="h-full w-full" aria-hidden="true">
      <rect x="12" y="7" width="48" height="32" rx="7" fill="rgba(240,200,90,.08)" stroke="rgba(240,200,90,.22)" transform="rotate(-7 36 23)" />
      <rect x="8" y="10" width="50" height="32" rx="7" fill="rgba(10,10,9,.95)" stroke="rgba(240,200,90,.46)" />
      <circle cx="20" cy="21" r="5" fill="rgba(240,200,90,.18)" stroke="rgba(240,200,90,.58)" />
      <path d="M16 35c6-7 11-7 16 0 7-11 14-11 21 0" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="1.2" />
      <circle cx="51" cy="17" r="2" fill="rgba(255,218,108,.9)"><animate attributeName="opacity" values=".25;1;.25" dur="1.5s" repeatCount="indefinite" /></circle>
    </svg>
  );
};

interface RobotStageProps {
  feature: FeatureKey;
  monitoringImage: string;
  compact?: boolean;
}

const RobotStage: React.FC<RobotStageProps> = ({ feature, monitoringImage, compact = false }) => {
  const { t } = useTranslation();
  const [selectedExpression, setSelectedExpression] = useState<ExpressionKey>('happy');
  const view = featureViews[feature];
  const scale = compact ? Math.max(0.72, view.scale * 0.72) : view.scale;
  const x = compact ? view.x * 0.72 : view.x;
  const y = compact ? view.y * 0.72 : view.y;
  const activeExpression: DisplayExpressionKey | null =
    feature === 'expressionSystem' ? selectedExpression : feature === 'privacyProtection' ? 'record' : null;
  const activeExpressionAssets = activeExpression ? expressionAssets[activeExpression] : null;
  const homeHelperDialogs = [
    {
      key: 'concert',
      icon: Ticket,
      request: t('home.features.longTermMemory.dialog.concertRequest'),
      response: t('home.features.longTermMemory.dialog.concertResponse'),
    },
    {
      key: 'reading',
      icon: BookOpen,
      request: t('home.features.longTermMemory.dialog.readingRequest'),
      response: t('home.features.longTermMemory.dialog.readingResponse'),
    },
  ] as const;

  return (
    <div className={`relative h-full w-full ${compact ? 'overflow-hidden' : 'overflow-visible'}`}>
      <div className="absolute left-1/2 top-[48%] h-[72%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/[0.055] blur-[90px]" />
      <div className="absolute bottom-[10%] left-1/2 h-[11%] w-[46%] -translate-x-1/2 rounded-full bg-black/70 blur-2xl" />

      <div
        className={`absolute left-1/2 top-1/2 aspect-[2000/2735] transition-transform duration-[1050ms] ease-[cubic-bezier(.22,.8,.2,1)] motion-reduce:transition-none ${
          compact ? 'h-[116%]' : 'h-[91%]'
        }`}
        style={{
          transform: `translate(calc(-50% + ${x}%), calc(-50% + ${y}%)) scale(${scale})`,
          transformOrigin: '50% 50%',
        }}
      >
        <img
          src={activeExpression ? robotImageWithEyes : robotImage}
          alt="WYNDME O1 Pro"
          loading="lazy"
          decoding="async"
          className="h-full w-full select-none object-contain"
          draggable={false}
        />

        {activeExpression && activeExpressionAssets && (
          <div className="pointer-events-none absolute inset-0 animate-[robotHello_360ms_ease-out]" aria-hidden="true">
            <span
              className="absolute left-[19.8%] top-[18.64%] h-[8.19%] w-[8.25%] overflow-hidden rounded-[50%]"
              style={{ transform: 'translate(-50%, -50%) rotate(0deg)' }}
            >
              <img
                key={`${activeExpression}-left`}
                src={activeExpressionAssets.left}
                alt=""
                draggable={false}
                className="h-full w-full select-none mix-blend-screen"
                style={{
                  transform: 'matrix(0.66, -0.152, -0.02, 0.7, 1.6, -1.6)',
                  clipPath: 'inset(0 round 8%)',
                }}
              />
            </span>
            <span
              className="absolute left-[47.34%] top-[14.69%] h-[8.92%] w-[9.85%] overflow-hidden rounded-[50%]"
              style={{ transform: 'translate(-50%, -50%) rotate(0deg)' }}
            >
              <img
                key={`${activeExpression}-right`}
                src={activeExpressionAssets.right}
                alt=""
                draggable={false}
                className="h-full w-full select-none mix-blend-screen"
                style={{
                  transform: 'matrix(0.66, -0.152, -0.02, 0.685, 1.6, -1.6)',
                  clipPath: 'inset(0 round 8%)',
                }}
              />
            </span>
          </div>
        )}
      </div>

      {feature === 'expressionSystem' && (
        <div
          className={`pointer-events-auto absolute left-[43%] z-40 -translate-x-1/2 animate-[robotHello_360ms_ease-out] ${
            compact ? 'top-[8%]' : 'top-[13%]'
          }`}
        >
          <div
            className="flex items-center gap-2 rounded-full border border-white/10 bg-black/70 p-2.5 shadow-[0_22px_62px_rgba(0,0,0,.48)] backdrop-blur-xl sm:gap-2.5 sm:p-3"
            role="toolbar"
            aria-label={t('home.features.expressionPicker')}
          >
            {expressionOptions.map(({ key, icon }) => {
              const selected = selectedExpression === key;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={selected}
                  aria-label={t(`home.features.expressions.${key}`)}
                  title={t(`home.features.expressions.${key}`)}
                  onClick={() => setSelectedExpression(key)}
                  className={`grid h-11 w-11 place-items-center rounded-full border text-xl transition-all duration-300 sm:h-12 sm:w-12 sm:text-2xl ${
                    selected
                      ? 'scale-110 border-brand-gold/70 bg-brand-gold/15 shadow-[0_0_30px_rgba(240,200,90,.32)]'
                      : 'border-white/[0.07] bg-white/[0.035] opacity-65 hover:border-white/20 hover:bg-white/[0.08] hover:opacity-100'
                  }`}
                >
                  <span aria-hidden="true">{icon}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div
        className={`pointer-events-none absolute inset-0 z-20 transition-[opacity,transform,filter] duration-700 ease-out ${
          feature === 'soundLocalization'
            ? 'scale-100 opacity-100 blur-0 delay-200'
            : 'scale-[0.96] opacity-0 blur-[3px] delay-0'
        }`}
        aria-hidden="true"
      >
        <span className="sound-array-ambience" />

        {microphoneNodes.map((node, index) => (
          <span
            key={index}
            className={`microphone-beacon microphone-beacon--${node.type}`}
            style={{ left: node.left, top: node.top, animationDelay: node.delay }}
          >
            <span
              className={`microphone-reception microphone-reception--${node.pickup}`}
              style={{ '--receive-delay': node.delay } as React.CSSProperties}
            >
              <span className="microphone-reception__wave microphone-reception__wave--near" />
              <span className="microphone-reception__wave microphone-reception__wave--middle" />
              <span className="microphone-reception__wave microphone-reception__wave--far" />
            </span>
            <img
              className="microphone-cutout"
              src={`/images/microphones/${node.asset}.png`}
              alt=""
              draggable={false}
            />
          </span>
        ))}
      </div>

      <div
        className={`pointer-events-none absolute inset-0 z-30 transition-[opacity,transform] duration-700 ease-[cubic-bezier(.22,.8,.2,1)] ${
          feature === 'remoteCompanion'
            ? 'translate-y-0 opacity-100 delay-250'
            : 'translate-y-3 opacity-0 delay-0'
        }`}
        aria-hidden="true"
      >
        <div
          className={`absolute left-[43%] -translate-x-1/2 -translate-y-1/2 drop-shadow-[0_28px_60px_rgba(0,0,0,.6)] ${
            compact ? 'top-[25%] w-[50%]' : 'top-[25%] w-[56%]'
          }`}
        >
          <img
            src={monitoringImage}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            className="block h-auto w-full select-none"
          />
        </div>
      </div>

      <div
        className={`pointer-events-none absolute inset-0 z-30 transition-[opacity,transform,filter] duration-700 ease-[cubic-bezier(.22,.8,.2,1)] ${
          feature === 'multimodalAI'
            ? 'translate-y-0 scale-100 opacity-100 blur-0 delay-200'
            : 'translate-y-3 scale-[0.96] opacity-0 blur-[3px] delay-0'
        }`}
        aria-hidden="true"
      >
        <svg className="absolute inset-0 h-full w-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
          {[
            'M16 18 Q29 20 43 39',
            'M17 31 Q30 33 43 39',
            'M69 18 Q57 20 43 39',
            'M69 31 Q56 33 43 39',
          ].map((path, index) => (
            <g key={path}>
              <path
                d={path}
                fill="none"
                stroke="rgba(240,200,90,.38)"
                strokeWidth="0.3"
                strokeDasharray="1.2 1.5"
              />
              <circle r="0.72" fill="rgba(255,220,118,.92)">
                <animateMotion
                  path={path}
                  dur={`${2.3 + index * 0.28}s`}
                  begin={`${index * 0.32}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          ))}
        </svg>

        <span className="absolute left-[43%] top-[39%] h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand-gold/15 bg-brand-gold/[0.025] shadow-[0_0_55px_rgba(240,200,90,.16)] sm:h-28 sm:w-28" />
        <span className="absolute left-[43%] top-[39%] h-16 w-16 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full border border-brand-gold/15 [animation-duration:2.8s]" />
        <span className="absolute left-[43%] top-[39%] flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-brand-gold/60 bg-[#0a0a09]/88 text-brand-gold shadow-[0_0_34px_rgba(240,200,90,.3)] backdrop-blur-xl sm:h-14 sm:w-14">
          <BrainCircuit className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.65} />
        </span>

        {multimodalInputs.map(({ key, left, top, delay }) => (
          <span
            key={key}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left, top }}
          >
            <span
              className="absolute inset-0 animate-ping rounded-full bg-brand-gold/15"
              style={{ animationDelay: delay, animationDuration: '2.6s' }}
            />
            <span className="relative block h-12 w-20 overflow-hidden rounded-2xl shadow-[0_0_28px_rgba(240,200,90,.18)] backdrop-blur-md sm:h-14 sm:w-24 lg:h-[4.2rem] lg:w-28">
              <MultimodalInputVisual type={key} />
            </span>
          </span>
        ))}
      </div>

      <div
        className={`pointer-events-none absolute inset-0 z-30 transition-[opacity,transform,filter] duration-700 ease-[cubic-bezier(.22,.8,.2,1)] ${
          feature === 'multilingual'
            ? 'translate-y-0 scale-100 opacity-100 blur-0 delay-200'
            : 'translate-y-3 scale-[0.96] opacity-0 blur-[3px] delay-0'
        }`}
        aria-hidden="true"
      >
        <span className="absolute left-[43%] top-[26%] h-36 w-[58%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-brand-gold/[0.035] blur-3xl" />

        {multilingualPhrases.map(({ text, left, top, delay }, index) => (
          <span
            key={text}
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ left, top }}
          >
            <span
              className={`multilingual-speech-bubble ${
                index === 1
                  ? 'border-brand-gold/45 bg-[#15130e]/90 px-5 py-3 text-sm text-white shadow-[0_14px_42px_rgba(240,200,90,.16)] sm:px-6 sm:py-3.5 sm:text-lg'
                  : 'border-white/12 bg-[#11100d]/82 px-4 py-2.5 text-xs text-white/82 shadow-[0_12px_34px_rgba(0,0,0,.4)] sm:px-5 sm:py-3 sm:text-base'
              }`}
              style={{ animationDelay: delay }}
            >
              <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-brand-gold align-middle shadow-[0_0_10px_rgba(240,200,90,.7)]" />
              <span className="align-middle">{text}</span>
            </span>
          </span>
        ))}
      </div>

      <div
        className={`pointer-events-none absolute inset-0 z-30 transition-[opacity,transform,filter] duration-700 ease-[cubic-bezier(.22,.8,.2,1)] ${
          feature === 'longTermMemory'
            ? 'translate-y-0 scale-100 opacity-100 blur-0 delay-200'
            : 'translate-y-3 scale-[0.96] opacity-0 blur-[3px] delay-0'
        }`}
        aria-hidden="true"
      >
        <span className="absolute left-[43%] top-[26%] h-48 w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-brand-gold/[0.04] blur-[54px]" />

        <div
          className={`absolute grid -translate-x-1/2 -translate-y-1/2 ${
            compact
              ? 'left-1/2 top-[29%] w-[92%] grid-cols-1 gap-2'
              : 'left-[40%] top-[27%] w-[80%] grid-cols-2 gap-4 xl:gap-5'
          }`}
        >
          {homeHelperDialogs.map(({ key, icon: Icon, request, response }, index) => (
            <article
              key={key}
              className={`home-helper-card rounded-[22px] border border-white/10 bg-[#0f0e0b]/82 shadow-[0_18px_48px_rgba(0,0,0,.42)] backdrop-blur-xl ${
                compact ? 'p-2.5' : 'p-3.5 xl:p-4'
              }`}
              style={{ animationDelay: `${index * 0.55 - 1.4}s` }}
            >
              <div className="mb-2.5 flex items-center justify-between">
                <span className="grid h-8 w-8 place-items-center rounded-full border border-brand-gold/25 bg-brand-gold/[0.08] text-brand-gold shadow-[0_0_20px_rgba(240,200,90,.12)]">
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.7} />
                </span>
                <span className="flex items-center gap-1.5" aria-hidden="true">
                  <span className="h-1 w-1 rounded-full bg-white/18" />
                  <span className="h-1 w-1 rounded-full bg-white/28" />
                  <span className="h-1 w-3 rounded-full bg-brand-gold/70" />
                </span>
              </div>

              <div className={`rounded-2xl rounded-tl-md border border-white/[0.07] bg-white/[0.045] text-white/78 ${compact ? 'px-3 py-2 text-[10px] leading-[1.55]' : 'px-3.5 py-2.5 text-[13px] leading-[1.65] xl:text-[15px]'}`}>
                {request}
              </div>

              <div className={`mt-2 rounded-2xl rounded-tr-md border border-brand-gold/20 bg-brand-gold/[0.075] text-white/86 ${compact ? 'px-3 py-2 text-[10px] leading-[1.55]' : 'px-3.5 py-2.5 text-[13px] leading-[1.65] xl:text-[15px]'}`}>
                <span className="mr-2 font-semibold text-brand-gold">{t('home.features.longTermMemory.dialog.robotName')}</span>
                {response}
              </div>
            </article>
          ))}
        </div>
      </div>

      <div
        className={`pointer-events-none absolute inset-0 z-30 transition-[opacity,transform,filter] duration-700 ease-[cubic-bezier(.22,.8,.2,1)] ${
          feature === 'personalization'
            ? 'translate-y-0 scale-100 opacity-100 blur-0 delay-200'
            : 'translate-y-3 scale-[0.96] opacity-0 blur-[3px] delay-0'
        }`}
        aria-hidden="true"
      >
        <span className="absolute left-[75%] top-[43%] h-52 w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] bg-brand-gold/[0.045] blur-[58px]" />

        <section
          className={`customization-studio absolute -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[28px] border border-white/10 bg-[#0e0d0a]/88 shadow-[0_24px_70px_rgba(0,0,0,.5)] backdrop-blur-2xl ${
            compact ? 'left-1/2 top-[42%] w-[92%] p-3' : 'left-[75%] top-[43%] w-[80%] p-4 xl:p-5'
          }`}
        >
          <div className="pointer-events-none absolute -right-16 -top-20 h-40 w-40 rounded-full bg-brand-gold/[0.1] blur-[48px]" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className={`grid shrink-0 place-items-center rounded-2xl border border-brand-gold/30 bg-brand-gold/[0.1] text-brand-gold shadow-[0_0_28px_rgba(240,200,90,.14)] ${compact ? 'h-9 w-9' : 'h-11 w-11'}`}>
                <Sparkles className={compact ? 'h-4 w-4' : 'h-5 w-5'} strokeWidth={1.7} />
              </span>
              <div className="min-w-0">
                <p className="text-[9px] font-semibold uppercase tracking-[0.24em] text-brand-gold/70 xl:text-[10px]">
                  {t('home.features.personalization.preview.badge')}
                </p>
                <h5 className={`mt-1 truncate font-display font-semibold tracking-[-0.03em] text-white ${compact ? 'text-base' : 'text-lg xl:text-xl'}`}>
                  {t('home.features.personalization.preview.roleName')}
                </h5>
              </div>
            </div>

            <span className="flex shrink-0 items-center gap-2 rounded-full border border-brand-gold/20 bg-brand-gold/[0.07] px-3 py-1.5 text-[9px] font-medium text-brand-gold/90 xl:text-[10px]">
              <SlidersHorizontal className="h-3 w-3" strokeWidth={1.8} />
              {t('home.features.personalization.preview.customized')}
            </span>
          </div>

          <div className={`relative mt-3 grid gap-2.5 ${compact ? 'grid-cols-[0.29fr_0.47fr_0.24fr]' : 'grid-cols-[0.26fr_0.5fr_0.24fr] xl:gap-3'}`}>
            <div className={`rounded-2xl border border-white/[0.07] bg-white/[0.04] ${compact ? 'p-2.5' : 'p-3 xl:p-3.5'}`}>
              <div className="flex items-center gap-2 text-white/35">
                <Volume2 className="h-3.5 w-3.5 text-brand-gold/75" strokeWidth={1.7} />
                <span className="text-[8px] uppercase tracking-[0.16em] xl:text-[9px]">
                  {t('home.features.personalization.preview.voiceLabel')}
                </span>
              </div>
              <p className={`mt-2 truncate font-medium text-white/86 ${compact ? 'text-[9px]' : 'text-[11px] xl:text-xs'}`}>
                {t('home.features.personalization.preview.voiceValue')}
              </p>
            </div>

            <div className={`min-w-0 rounded-2xl border border-white/[0.07] bg-white/[0.04] ${compact ? 'p-2.5' : 'p-3 xl:p-3.5'}`}>
              <div className="flex items-center gap-2 text-white/35">
                <FileText className="h-3.5 w-3.5 text-brand-gold/75" strokeWidth={1.7} />
                <span className="text-[8px] uppercase tracking-[0.16em] xl:text-[9px]">
                  {t('home.features.personalization.preview.roleIntroLabel')}
                </span>
              </div>
              <p
                className={`mt-2 overflow-hidden text-white/72 ${compact ? 'text-[8px] leading-[1.5]' : 'text-[10px] leading-[1.55] xl:text-[11px]'}`}
                style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}
              >
                {t('home.features.personalization.preview.roleIntro')}
              </p>
            </div>

            <div className={`min-w-0 rounded-2xl border border-white/[0.07] bg-white/[0.04] ${compact ? 'p-2.5' : 'p-3 xl:p-3.5'}`}>
              <div className="flex items-center gap-2 text-white/35">
                <Languages className="h-3.5 w-3.5 text-brand-gold/75" strokeWidth={1.7} />
                <span className="text-[8px] uppercase tracking-[0.16em] xl:text-[9px]">
                  {t('home.features.personalization.preview.languageLabel')}
                </span>
              </div>
              <p className={`mt-2 truncate font-medium text-white/86 ${compact ? 'text-[9px]' : 'text-[11px] xl:text-xs'}`}>
                {t('home.features.personalization.preview.languageValue')}
              </p>
            </div>
          </div>

          <div className="relative mt-3 grid grid-cols-2 gap-2.5 xl:gap-3">
            <div className={`flex items-center justify-between rounded-2xl border border-white/[0.07] bg-black/25 ${compact ? 'px-2.5 py-2' : 'px-3.5 py-2.5'}`}>
              <span className="flex min-w-0 items-center gap-2 text-white/65">
                <Globe2 className="h-3.5 w-3.5 shrink-0 text-brand-gold/75" strokeWidth={1.7} />
                <span className={`truncate ${compact ? 'text-[8px]' : 'text-[10px] xl:text-[11px]'}`}>
                  {t('home.features.personalization.preview.networkSearch')}
                </span>
              </span>
              <span className="ml-2 flex shrink-0 items-center gap-1.5 rounded-full bg-brand-gold/[0.08] px-2 py-1 text-[8px] font-medium text-brand-gold/85 xl:text-[9px]">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-gold shadow-[0_0_8px_rgba(240,200,90,.75)]" />
                {t('home.features.personalization.preview.enabled')}
              </span>
            </div>

            <div className={`flex items-center justify-between rounded-2xl border border-white/[0.07] bg-black/25 ${compact ? 'px-2.5 py-2' : 'px-3.5 py-2.5'}`}>
              <span className="flex min-w-0 items-center gap-2 text-white/65">
                <Upload className="h-3.5 w-3.5 shrink-0 text-brand-gold/75" strokeWidth={1.7} />
                <span className={`truncate ${compact ? 'text-[8px]' : 'text-[10px] xl:text-[11px]'}`}>
                  {t('home.features.personalization.preview.knowledgeBase')}
                </span>
              </span>
              <span className="ml-2 shrink-0 text-[8px] text-white/35 xl:text-[9px]">
                {t('home.features.personalization.preview.uploadFile')}
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export const FeaturesSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0] as keyof typeof monitoringImages;
  const monitoringImage = monitoringImages[language] ?? monitoringImages.en;

  useEffect(() => {
    let animationFrame = 0;

    const updateActiveFeature = () => {
      animationFrame = 0;
      const focusLine = window.innerHeight * 0.48;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      itemRefs.current.forEach((item, index) => {
        if (!item) return;
        const bounds = item.getBoundingClientRect();
        const itemCenter = bounds.top + bounds.height / 2;
        const distance = Math.abs(itemCenter - focusLine);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex((current) => (current === closestIndex ? current : closestIndex));
    };

    const requestUpdate = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(updateActiveFeature);
    };

    requestUpdate();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  const activeFeature = featureKeys[activeIndex];

  return (
    <section id="features" className="relative overflow-x-clip bg-[#080807] text-white">
      <div className="pointer-events-none absolute left-1/2 top-[18vh] h-[34rem] w-[54rem] -translate-x-1/2 rounded-full bg-brand-gold/[0.06] blur-[190px]" />
      <div className="pointer-events-none absolute -left-48 top-[110vh] h-[36rem] w-[36rem] rounded-full bg-brand-gold/[0.055] blur-[170px]" />
      <div className="pointer-events-none absolute -right-52 bottom-[18rem] h-[42rem] w-[42rem] rounded-full bg-[#154964]/[0.08] blur-[190px]" />

      <header className="container relative mx-auto flex min-h-[100svh] items-center justify-center px-5 py-28 text-center sm:px-6 md:px-12 md:py-36">
        <div className="relative">
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-px w-[min(78vw,58rem)] -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-transparent via-brand-gold/20 to-transparent" />
          <h2 className="relative mx-auto max-w-6xl whitespace-pre-line font-display text-[clamp(3.2rem,7.6vw,8.4rem)] font-bold leading-[0.96] tracking-[-0.055em]">
            {t('home.features.title')}
          </h2>
        </div>
      </header>

      <div className="container relative mx-auto px-5 pb-24 sm:px-6 md:px-12 md:pb-36">
        <div className="mx-auto max-w-7xl lg:grid lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)] lg:gap-12 xl:gap-20">
          <div>
            {featureKeys.map((key, index) => {
              const active = index === activeIndex;

              return (
                <article
                  key={key}
                  ref={(element) => {
                    itemRefs.current[index] = element;
                  }}
                  data-feature-index={index}
                  tabIndex={0}
                  aria-current={active ? 'true' : undefined}
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  className={`group flex min-h-[64vh] scroll-mt-28 flex-col justify-center py-14 outline-none transition-[opacity,transform] duration-500 lg:min-h-[72vh] lg:py-20 ${
                    active
                      ? 'translate-x-0 opacity-100'
                      : '-translate-x-3 opacity-30 hover:translate-x-0 hover:opacity-70 focus:translate-x-0 focus:opacity-100'
                  }`}
                >
                  <div className="max-w-xl">
                    <span
                      className={`mb-8 block h-1.5 rounded-full bg-brand-gold transition-[width,opacity] duration-500 ${
                        active ? 'w-14 opacity-100' : 'w-7 opacity-35'
                      }`}
                    />
                    <h4 className="text-pretty font-display text-4xl font-bold leading-[1.04] tracking-[-0.04em] sm:text-5xl lg:text-[3.35rem] xl:text-6xl">
                      {t(`home.features.${key}.title`)}
                    </h4>
                    <p className="mt-7 max-w-lg text-pretty text-base leading-8 text-white/58 md:text-lg md:leading-9">
                      {t(`home.features.${key}.desc`)}
                    </p>
                  </div>

                  <div className="mt-8 h-[25rem] overflow-hidden lg:hidden">
                    <RobotStage feature={key} monitoringImage={monitoringImage} compact />
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="relative hidden lg:block" aria-live="polite">
            <div className="sticky top-0 h-screen overflow-visible">
              <div className="pointer-events-none absolute inset-y-0 -right-[34%] left-0">
                <RobotStage feature={activeFeature} monitoringImage={monitoringImage} />
              </div>

              <div className="pointer-events-none absolute bottom-10 right-0 text-right">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/30">WYNDME O1 Pro</p>
                <div className="mt-3 flex justify-end gap-1.5">
                  {featureKeys.map((key, index) => (
                    <span
                      key={key}
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        index === activeIndex ? 'w-7 bg-brand-gold' : 'w-1.5 bg-white/18'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};
